import type { LandingSourceKey } from '../../utils/landingSourceKey';

/**
 * Public entry copy for Resilience Circle. Name and email are collected at account creation.
 * Source: company model `jason-safford-resiliance-circle`.
 * Activity `ai-bsc-act-rc-016-design-lead-magnet` under
 * `ai-bsc-imp-rc-016-demand-frontend` (waypoint `ai-wp-rc-016-demand-front-end-v3`).
 * Pillars and situations are the launch avatar / Resilience Member offer, not new claims.
 */

export const RC_LEAD_MAGNET_ACTIVITY_REF = 'ai-bsc-act-rc-016-design-lead-magnet';

export const RC_HEADLINE = 'You are not breaking because of pressure.';
export const RC_HEADLINE_SECONDARY = 'You are carrying it without structure.';
export const RC_ACTIVATING_MESSAGE = `${RC_HEADLINE} ${RC_HEADLINE_SECONDARY}`;

export type RcSituationId = 'pressure-spike' | 'fragility-exposure' | 'identity-tension';

export interface RcSituation {
  id: RcSituationId;
  title: string;
  summary: string;
}

export const RC_SITUATIONS: readonly RcSituation[] = [
  {
    id: 'pressure-spike',
    title: 'Pressure spike',
    summary:
      'Growth has arrived, and it is still tied to your energy. The opportunity is here. The structure to absorb it is not.',
  },
  {
    id: 'fragility-exposure',
    title: 'Fragility exposure',
    summary:
      'Missed commitments and uneven output are starting to cost trust — with yourself, your team, and your customers.',
  },
  {
    id: 'identity-tension',
    title: 'Identity tension',
    summary:
      'The business is working, and you can already see that this operating mode will not hold the next level.',
  },
];

export type RcPillarId = 'mental' | 'physical' | 'emotional' | 'spiritual' | 'imaginative';

export type RcPillarMark = 'pressure' | 'uneven' | 'holding';

export interface RcPillar {
  id: RcPillarId;
  name: string;
  underPressure: string;
  installed: string;
}

/** Five Pillars from the Resilience Member assessment (Mental, Physical, Emotional, Spiritual, Imaginative). */
export const RC_PILLARS: readonly RcPillar[] = [
  {
    id: 'mental',
    name: 'Mental Precision',
    underPressure: 'Reactive thinking, context-switching, and no clear priority.',
    installed: 'Clear decisions and focused execution.',
  },
  {
    id: 'physical',
    name: 'Physical Mastery',
    underPressure: 'Inconsistent energy, poor recovery, and a broken daily rhythm.',
    installed: 'Stable energy and a rhythm you can repeat.',
  },
  {
    id: 'emotional',
    name: 'Emotional Power',
    underPressure: 'Pressure stacks up as irritability, suppression, or quiet volatility.',
    installed: 'Composure under pressure, with less volatility.',
  },
  {
    id: 'spiritual',
    name: 'Spiritual Alignment',
    underPressure: 'Purpose drifts. Decisions run in survival mode.',
    installed: 'A reconnection to the mission and internal alignment.',
  },
  {
    id: 'imaginative',
    name: 'Imaginative Strategy',
    underPressure: 'No room for strategy. The day is a short-term reaction loop.',
    installed: 'Strategic thinking and a plan that faces forward.',
  },
];

export const RC_PILLAR_MARKS: readonly { id: RcPillarMark; label: string }[] = [
  { id: 'pressure', label: 'Under pressure' },
  { id: 'uneven', label: 'Uneven' },
  { id: 'holding', label: 'Holding' },
];

/** Prospect-agent code: this person is a Resilience Circle prospect, not a matched ICP. */
export const RC_PROSPECT_CODE = 'RC';

export const RC_PROSPECT_KICKOFF_TEXT_KEY = 'krysalis_rc_prospect_kickoff_text';

export function situationById(id: RcSituationId): RcSituation {
  const found = RC_SITUATIONS.find((row) => row.id === id);
  if (!found) {
    return RC_SITUATIONS[0];
  }
  return found;
}

export function gapPillars(marks: Partial<Record<RcPillarId, RcPillarMark>>): RcPillar[] {
  return RC_PILLARS.filter((pillar) => marks[pillar.id] === 'pressure');
}

/** Source key carried into the prospect agent. Name and email come from account creation. */
export function buildResilienceCircleSourceKey(): LandingSourceKey {
  return {
    prospect_code: RC_PROSPECT_CODE,
    conversation_starter: RC_ACTIVATING_MESSAGE,
    target_product: 'resilience-circle',
    source_magnet: 'resilience-circle-landing',
    source_channel: 'founder-group',
  };
}

/** First user turn so the prospect agent opens the RC ICP interview. */
export function buildRcProspectKickoff(): string {
  return `I'm here from the Resilience Circle landing. Prospect code: ${RC_PROSPECT_CODE}.`;
}

export function persistRcProspectKickoff(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(RC_PROSPECT_KICKOFF_TEXT_KEY, buildRcProspectKickoff());
}

export function readRcProspectKickoff(): string {
  if (typeof sessionStorage === 'undefined') return buildRcProspectKickoff();
  const stored = sessionStorage.getItem(RC_PROSPECT_KICKOFF_TEXT_KEY)?.trim();
  return stored || buildRcProspectKickoff();
}

export function isRcProspectSourceKey(key: LandingSourceKey | null | undefined): boolean {
  return key?.prospect_code?.trim().toUpperCase() === RC_PROSPECT_CODE;
}
